import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'afl-design-system';

import { useIsDarkTheme } from 'common/react/hooks/use-is-dark-theme';

// Folders whose crests live in the shared `svg-output` sprite rather than
// standalone per-icon SVGs.
const SPRITE_FOLDERS = new Set(['aflw', 'state-league']);

// DS icon sizes, smallest first.
const ICON_SIZES = [
    'x_small',
    'small',
    'medium',
    'large',
    'x_large',
    'x2_large'
];

// Shield stand-ins are drawn edge to edge (crests carry ~12% internal padding),
// so at the same size token a shield reads a size larger - step it down one.
const stepSizeDown = (size) => {
    const index = ICON_SIZES.indexOf(size);

    return index > 0 ? ICON_SIZES[index - 1] : size;
};

// Builds the sprite href: the shared `svg-output` sprite (optionally nested in
// a folder) for AFLM/AFLW/state-league crests, else a standalone per-icon SVG.
const getIconPath = (folder, iconName) => {
    const cdn = PULSE.app.environment.cdn;

    if (folder && !SPRITE_FOLDERS.has(folder)) {
        return `${cdn}i/${folder}/${iconName}.svg#icn-${iconName}`;
    }

    const folderPath = folder ? `${folder}/` : '';

    return `${cdn}i/svg-output/${folderPath}icons.svg#icn-${iconName}`;
};

/**
 * Renders a team crest via the DS `Icon` component, pointed at aflm's own sprite.
 *
 * @param {object} props - Props.
 * @param {string} [props.providerId] - ProviderId of the team to display.
 * @param {string} [props.teamAbbreviation] - Team abbreviation; overrides providerId resolution.
 * @param {string} [props.className] - Class(es) for the DS Icon wrapper.
 * @param {string} [props.theme] - Theme modifier e.g. 'notext'/'light'.
 * @param {string} [props.fallbackIcon] - Fallback icon modifier e.g. 'shield-grey'.
 * @param {string} [props.size] - DS ICON_VARIANTS.SIZE value, defaults to 'medium'.
 * @param {string} [props.label] - Accessible label; exposes the icon to AT when set.
 * @returns {React.ReactElement} Component.
 */
export const DsTeamIcon = ({
    providerId,
    teamAbbreviation,
    className = '',
    fallbackIcon = '',
    theme,
    size,
    label
}) => {
    const [iconRef, isDarkTheme] = useIsDarkTheme();

    // Dark mode needs a different crest asset, not just different tokens, so it
    // resolves through the same 'light' theme modifier the light-on-dark
    // contexts already use (see the `-light` crests in i/svg-files).
    const resolvedTheme = isDarkTheme ? 'light' : theme;

    let teamAbbr = '';

    if (teamAbbreviation?.length > 0) {
        teamAbbr = teamAbbreviation.toLowerCase();
    } else {
        teamAbbr = PULSE.app.common.team.getTeamAbbr(providerId);
    }

    let folder = '';

    if (PULSE.app.common.team.getAFLWSpecificIcons().includes(providerId)) {
        folder = 'aflw';
    }

    const stateLeague =
        PULSE.app.common.team
            .getStateLeagueTeamsProviders()
            .includes(providerId) ||
        PULSE.app.common.team.getStateLeagueTeams().includes(teamAbbr);

    let modifier = '';

    const hasTheme = resolvedTheme && resolvedTheme !== '';
    const themeTeams = PULSE.app.common.team.getTeamsByLogoTheme(resolvedTheme);

    if (
        !PULSE.app.common.team
            .getTeamsWithDefaultThemeOnly()
            .includes(teamAbbr) &&
        (!themeTeams?.length || themeTeams.includes(teamAbbr))
    ) {
        modifier = hasTheme ? `-${resolvedTheme}` : '';
    }

    const isFallback = teamAbbr === null;

    const iconName = isFallback
        ? fallbackIcon || 'shield'
        : `aflc-${teamAbbr}${modifier}`;

    const resolvedFolder = stateLeague ? 'state-league' : folder;
    const iconPath = getIconPath(resolvedFolder, iconName);

    const a11yProps = label ? { role: 'img', label } : { role: 'presentation' };

    return (
        <Icon
            ref={iconRef}
            className={className}
            iconPath={iconPath}
            size={isFallback ? stepSizeDown(size) : size}
            {...a11yProps}
        />
    );
};

DsTeamIcon.propTypes = {
    providerId: PropTypes.string,
    teamAbbreviation: PropTypes.string,
    className: PropTypes.string,
    theme: PropTypes.string,
    fallbackIcon: PropTypes.string,
    size: PropTypes.string,
    label: PropTypes.string
};
